/**
 * Master KOMPONEN upah borongan — VONIS-24SEP-BORONGAN (Owner 24 Sep 2026).
 *
 * Komponen: dasar jersey, dasar celana, tambahan per jenis kerah, tambahan lengan panjang,
 * tambahan pola raglan. Satu baris per komponen (per jenis kerah untuk KERAH).
 * VONIS-24SEP-SORE butir 4: tiap kerah aktif di tiap region WAJIB ditandai eksplisit — "tambahan Rp …"
 * atau "tanpa tambahan"; kerah belum ditandai membuat FA "tarif belum lengkap". Tabel status kerah per
 * region di bawah daftar komponen supaya tidak ada yang terlewat. Tarif per region diisi lewat "Tambah versi tarif"
 * (tarif pertama wajib persetujuan Owner — BUSINESS-20; KC ±10% sendiri, di atasnya Owner).
 * Komponen yang sudah dipakai tidak dihapus — hanya dinonaktifkan (K29a).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { StatusBadge } from "./StatusBadge";
import { toast } from "sonner";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { konfirmasi, mintaTeks } from "../../lib/dialogAplikasi";
import {
  cariKomponenKerah, halamanKerah, kalimatTandaiMassal, komponenDariGalat, saringKerah, wilayahKerah,
} from "../../lib/kerahTambahan";

const JENIS = [
  { kode: "DASAR_JERSEY", label: "Dasar jersey (per potong atasan)" },
  { kode: "DASAR_CELANA", label: "Dasar celana (per potong celana)" },
  { kode: "KERAH", label: "Tambahan kerah (per jersey berkerah itu)" },
  { kode: "LENGAN_PANJANG", label: "Tambahan lengan panjang (per jersey lengan panjang)" },
  { kode: "POLA_RAGLAN", label: "Tambahan pola raglan (per jersey FA raglan)" },
];
const LABEL_JENIS = Object.fromEntries(JENIS.map((j) => [j.kode, j.label]));

function pesan(e, cadangan) {
  const d = e?.response?.data?.detail;
  return (d && (d.message || (typeof d === "string" ? d : null))) || cadangan;
}

export default function MasterKomponenBorongan({ bolehTulis, onBerubah }) {
  const [komponen, setKomponen] = useState([]);
  const [kerah, setKerah] = useState([]);
  const [jenis, setJenis] = useState("");
  const [collarId, setCollarId] = useState("");
  const [sibuk, setSibuk] = useState(false);
  const [statusKerah, setStatusKerah] = useState(null);
  // T95: tab wilayah · saringan "Belum ditandai saja" (bawaan) · cari · paginasi · pilihan aksi massal.
  const [regionAktif, setRegionAktif] = useState(null);
  const [saring, setSaring] = useState("BELUM");
  const [cari, setCari] = useState("");
  const [halaman, setHalaman] = useState(1);
  const [pilih, setPilih] = useState(() => new Set());
  const [sibukMassal, setSibukMassal] = useState(false);
  const wilayah = useMemo(() => wilayahKerah(statusKerah?.items), [statusKerah]);
  useEffect(() => {
    if (!wilayah.length) return;
    if (!wilayah.some((w) => w.region_id === regionAktif)) {
      // Bawaan: wilayah pertama yang masih punya kerah belum ditandai.
      setRegionAktif((wilayah.find((w) => w.belum > 0) || wilayah[0]).region_id);
    }
  }, [wilayah, regionAktif]);
  const barisTampil = useMemo(() => saringKerah(statusKerah?.items, { regionId: regionAktif, saring, cari }),
    [statusKerah, regionAktif, saring, cari]);
  const halamanIni = halamanKerah(barisTampil, halaman);
  const bisaDipilih = halamanIni.rows.filter((b) => b.status !== "TANPA_TAMBAHAN");

  const muat = useCallback(async () => {
    try {
      const r = await api.get("/master/sewing-work-types");
      setKomponen((r.data.items || []).filter((w) => w.jenis_komponen));
    } catch (e) {
      toast.error(pesan(e, "Gagal memuat komponen"));
    }
  }, []);

  const muatStatusKerah = useCallback(async () => {
    try {
      const r = await api.get("/master/sewing-rates/kerah-status");
      setStatusKerah(r.data);
    } catch (e) {
      setStatusKerah(null);
      toast.error(apiErrorMessage(e, "Status kerah per wilayah gagal dimuat"));
    }
  }, []);

  useEffect(() => { muat(); muatStatusKerah(); }, [muat, muatStatusKerah]);

  const tandaiTanpa = async (b) => {
    if (!(await konfirmasi(`Tandai ${b.collar_name} di ${b.region_name} "tanpa tambahan" mulai hari ini?`))) return;
    try {
      await api.post("/master/sewing-rates/kerah-tanpa-tambahan", { region_id: b.region_id, collar_id: b.collar_id });
      toast.success("Kerah ditandai tanpa tambahan.");
      await muatStatusKerah();
      if (onBerubah) onBerubah();
    } catch (e) {
      toast.error(pesan(e, "Gagal menandai kerah"));
    }
  };

  const tandaiTanpaMassal = async () => {
    const baris = (statusKerah?.items || []).filter((b) => b.region_id === regionAktif && pilih.has(b.collar_id)
      && b.status !== "TANPA_TAMBAHAN");
    if (!baris.length) return;
    if (!(await konfirmasi(kalimatTandaiMassal(baris), { judul: "Tandai tanpa tambahan", ya: `Tandai ${baris.length} kerah` }))) return;
    setSibukMassal(true);
    const gagal = [];
    for (const b of baris) {
      try {
        // Satu per kerah lewat pintu yang sama dengan tombol per baris (Owner saja, audit per kerah).
        await api.post("/master/sewing-rates/kerah-tanpa-tambahan", { region_id: b.region_id, collar_id: b.collar_id });
      } catch (e) {
        gagal.push(`${b.collar_name} (${pesan(e, "gagal")})`);
      }
    }
    setSibukMassal(false);
    setPilih(new Set());
    if (gagal.length) toast.error(`${baris.length - gagal.length} kerah ditandai; gagal: ${gagal.join("; ")}`);
    else toast.success(`${baris.length} kerah ditandai tanpa tambahan.`);
    await muatStatusKerah();
    if (onBerubah) onBerubah();
  };

  const isiTambahan = async (b) => {
    const teks = await mintaTeks(`Tambahan per jersey untuk ${b.collar_name} di ${b.region_name} (Rp, bilangan bulat):`, { multiBaris: false });
    if (teks === null) return;
    const n = Number(String(teks).replace(/[^0-9]/g, ""));
    if (!Number.isInteger(n) || n <= 0) { toast.error("Tambahan wajib bilangan bulat > 0."); return; }
    try {
      // T72: pakai komponen KERAH yang sudah ada (baris status ATAU daftar komponen) — jangan membuat ulang.
      let k = cariKomponenKerah(b, komponen);
      if (!k) {
        try {
          const w = await api.post("/master/sewing-work-types", {
            code: `KERAH-${String(b.collar_code).toUpperCase()}`.slice(0, 40),
            name: `Tambahan kerah ${b.collar_name}`, jenis_komponen: "KERAH", collar_id: b.collar_id,
          });
          k = { id: w.data.id, aktif: true };
        } catch (e) {
          k = komponenDariGalat(e);
          if (!k) throw e;
        }
      }
      if (!k.aktif) {
        if (!(await konfirmasi(`Komponen tambahan kerah ${b.collar_name} sedang nonaktif. Aktifkan lagi lalu simpan tambahannya?`))) return;
        await api.patch(`/master/sewing-work-types/${k.id}`, { is_active: true });
      }
      const r = await api.post("/master/sewing-rates", {
        region_id: b.region_id, work_type_id: k.id, rate_per_pcs_idr: n,
        reason: `Tambahan kerah ${b.collar_name} di ${b.region_name}`,
      });
      toast.success(r.status === 202 ? "Diajukan — menunggu Owner." : "Tambahan kerah tersimpan.");
      if (onBerubah) onBerubah();
    } catch (e) {
      toast.error(pesan(e, "Gagal menyimpan tambahan kerah"));
    } finally {
      // Dimuat ulang juga saat gagal: komponen yang sempat terbuat tetap terlihat, klik berikutnya memakainya.
      await muat(); await muatStatusKerah();
    }
  };
  useEffect(() => {
    if (!bolehTulis) return;
    api.get("/master/collar").then(({ data }) => setKerah(data.items || [])).catch((e) => { setKerah([]); toast.error(apiErrorMessage(e, "Daftar kerah gagal dimuat")); });
  }, [bolehTulis]);

  const ada = (kode, cid) => komponen.some((k) => k.jenis_komponen === kode && (kode !== "KERAH" || k.collar_id === cid));

  const tambah = async () => {
    if (!jenis) return;
    let code = jenis.replace(/_/g, "-");
    let name = LABEL_JENIS[jenis].split(" (")[0];
    if (jenis === "KERAH") {
      const c = kerah.find((x) => x.id === collarId);
      if (!c) { toast.error("Pilih jenis kerah."); return; }
      code = `KERAH-${String(c.collar_code).toUpperCase()}`.slice(0, 40);
      name = `Tambahan kerah ${c.collar_name}`;
    }
    setSibuk(true);
    try {
      await api.post("/master/sewing-work-types", {
        code, name, jenis_komponen: jenis, collar_id: jenis === "KERAH" ? collarId : null,
      });
      toast.success("Komponen ditambahkan. Isi tarif per wilayah lewat \"Tambah versi tarif\".");
      setJenis(""); setCollarId("");
      await muat();
      if (onBerubah) onBerubah();
    } catch (e) {
      toast.error(pesan(e, "Gagal menambah komponen"));
    } finally {
      setSibuk(false);
    }
  };

  const ubahAktif = async (k) => {
    try {
      await api.patch(`/master/sewing-work-types/${k.id}`, { is_active: !k.is_active });
      await muat();
      if (onBerubah) onBerubah();
    } catch (e) {
      toast.error(pesan(e, "Gagal mengubah status komponen"));
    }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="master-komponen-borongan">
      <div>
        <h2 className="text-base font-medium">Komponen upah borongan</h2>
        <p className="text-sm text-muted-foreground">
          Upah jahit tiap FA dihitung otomatis: dasar + tambahan yang berlaku × jumlah potong, dengan tarif
          region yang berlaku pada tanggal FA dibuat. Tiap kerah aktif wajib ditandai di tiap region: tambahan Rp
          atau tanpa tambahan — kerah yang belum ditandai membuat FA "tarif belum lengkap".
        </p>
      </div>
      <div className="space-y-1">
        {komponen.map((k) => (
          <div key={k.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted px-3 py-2 text-sm">
            <span>
              <b>{k.name}</b>
              <span className="ml-2 text-xs text-muted-foreground">{LABEL_JENIS[k.jenis_komponen]}</span>
              {k.collar_name && <span className="ml-1 text-xs text-muted-foreground">· {k.collar_name}</span>}
            </span>
            <span className="flex items-center gap-2">
              <StatusBadge tone={k.is_active ? "success" : "neutral"}>{k.is_active ? "Aktif" : "Nonaktif"}</StatusBadge>
              {bolehTulis && (
                <Button size="sm" variant="ghost" onClick={() => ubahAktif(k)}>
                  {k.is_active ? "Nonaktifkan" : "Aktifkan"}
                </Button>
              )}
            </span>
          </div>
        ))}
        {komponen.length === 0 && <p className="text-sm text-muted-foreground">Belum ada komponen.</p>}
      </div>
      {bolehTulis && (
        <div className="flex flex-wrap items-end gap-2">
          <select className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  value={jenis} onChange={(e) => setJenis(e.target.value)} data-testid="pilih-jenis-komponen">
            <option value="">Tambah komponen…</option>
            {JENIS.filter((j) => j.kode === "KERAH" || !ada(j.kode)).map((j) => (
              <option key={j.kode} value={j.kode}>{j.label}</option>
            ))}
          </select>
          {jenis === "KERAH" && (
            <select className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
                    value={collarId} onChange={(e) => setCollarId(e.target.value)}>
              <option value="">Pilih kerah…</option>
              {kerah.filter((c) => !ada("KERAH", c.id)).map((c) => (
                <option key={c.id} value={c.id}>{c.collar_name}</option>
              ))}
            </select>
          )}
          <Button size="sm" disabled={!jenis || sibuk} onClick={tambah}>Tambah</Button>
        </div>
      )}

      {statusKerah && (
        <div className="space-y-2" data-testid="status-kerah-region">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-medium">Status kerah per wilayah (hari ini)</h3>
            {statusKerah.belum_ditandai > 0 ? (
              <StatusBadge tone="danger">{statusKerah.belum_ditandai} belum ditandai</StatusBadge>
            ) : (
              <StatusBadge tone="success">Semua ditandai</StatusBadge>
            )}
          </div>
          {/* T111d (2 Nov 2026): satu kalimat pembeda — tambahan UPAH per kerah ≠ tambahan HARGA JUAL (Master › Collar). */}
          <p className="text-sm text-muted-foreground" data-testid="penjelas-status-kerah">
            Tambahan <strong>upah jahit</strong> per jersey untuk tiap kerah, dibayar ke penjahit di atas tarif dasar.
            Ini berbeda dengan Master › Collar / Kerah, yang menambah <strong>harga jual ke customer</strong>.
          </p>
          {/* T95 (a): tab per wilayah dengan hitungan "belum ditandai". */}
          <div className="flex flex-wrap gap-2" role="tablist" data-testid="tab-wilayah-kerah">
            {wilayah.map((w) => (
              <button key={w.region_id} type="button" role="tab" aria-selected={w.region_id === regionAktif}
                      onClick={() => { setRegionAktif(w.region_id); setHalaman(1); setPilih(new Set()); }}
                      data-testid={`tab-kerah-${w.region_id}`}
                      className={"rounded-lg border px-3 py-1.5 text-sm " + (w.region_id === regionAktif
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-input bg-background text-foreground hover:bg-muted")}>
                {w.region_name}
                <span className="ml-1 text-xs">{w.belum > 0 ? `· ${w.belum} belum ditandai` : "· lengkap"}</span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm text-foreground"
                    value={saring} onChange={(e) => { setSaring(e.target.value); setHalaman(1); }}
                    data-testid="saring-kerah" aria-label="Saring status kerah">
              <option value="BELUM">Belum ditandai saja</option>
              <option value="SEMUA">Semua kerah</option>
            </select>
            <input className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm text-foreground"
                   placeholder="Cari kerah…" value={cari} aria-label="Cari kerah"
                   onChange={(e) => { setCari(e.target.value); setHalaman(1); }} data-testid="cari-kerah" />
            {bolehTulis && (
              /* T95 (b): aksi massal "Tanpa tambahan" — dialog menyebut jumlah & nama kerah (pagar salah pencet). */
              <Button size="sm" variant="outline" disabled={!pilih.size || sibukMassal}
                      onClick={tandaiTanpaMassal} data-testid="tandai-massal-tanpa">
                {sibukMassal ? "Menyimpan…" : `Tandai yang dipilih: Tanpa tambahan (${pilih.size})`}
              </Button>
            )}
          </div>
          {barisTampil.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="kerah-kosong">
              {saring === "BELUM" ? "Semua kerah di wilayah ini sudah ditandai." : "Tidak ada kerah yang cocok."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    {bolehTulis && (
                      <th className="py-1 pr-2 w-8">
                        <input type="checkbox" aria-label="Pilih semua di halaman ini"
                               checked={bisaDipilih.length > 0 && bisaDipilih.every((b) => pilih.has(b.collar_id))}
                               onChange={(e) => setPilih((p) => {
                                 const baru = new Set(p);
                                 bisaDipilih.forEach((b) => (e.target.checked ? baru.add(b.collar_id) : baru.delete(b.collar_id)));
                                 return baru;
                               })} data-testid="pilih-semua-kerah" />
                      </th>
                    )}
                    <th className="py-1 pr-2">Kerah</th>
                    <th className="py-1 pr-2">Status</th>{bolehTulis && <th />}
                  </tr>
                </thead>
                <tbody>
                  {halamanIni.rows.map((b) => (
                    <tr key={`${b.region_id}-${b.collar_id}`} className="border-t border-border"
                        data-testid={`kerah-${b.region_id}-${b.collar_id}`}>
                      {bolehTulis && (
                        <td className="py-1 pr-2">
                          {b.status !== "TANPA_TAMBAHAN" && (
                            <input type="checkbox" aria-label={`Pilih ${b.collar_name}`} checked={pilih.has(b.collar_id)}
                                   onChange={(e) => setPilih((p) => {
                                     const baru = new Set(p);
                                     if (e.target.checked) baru.add(b.collar_id); else baru.delete(b.collar_id);
                                     return baru;
                                   })} />
                          )}
                        </td>
                      )}
                      <td className="py-1 pr-2">{b.collar_name}</td>
                      <td className="py-1 pr-2">
                        {b.status === "TAMBAHAN" && <StatusBadge tone="success">Tambahan Rp{Number(b.tambahan_idr || 0).toLocaleString("id-ID")}</StatusBadge>}
                        {b.status === "TANPA_TAMBAHAN" && <StatusBadge tone="neutral">Tanpa tambahan</StatusBadge>}
                        {b.status === "BELUM_DITANDAI" && <StatusBadge tone="danger">Belum ditandai</StatusBadge>}
                      </td>
                      {bolehTulis && (
                        <td className="py-1 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => isiTambahan(b)}>Tambahan Rp…</Button>
                          {b.status !== "TANPA_TAMBAHAN" && (
                            <Button size="sm" variant="ghost" onClick={() => tandaiTanpa(b)}>Tanpa tambahan</Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* T95 (c): paginasi di dalam kartu bila lebih dari 20 baris. */}
          {halamanIni.jumlah > 1 && (
            <div className="flex items-center justify-end gap-2 text-sm" data-testid="paginasi-kerah">
              <Button size="sm" variant="outline" disabled={halamanIni.halaman <= 1}
                      onClick={() => setHalaman(halamanIni.halaman - 1)}>Sebelumnya</Button>
              <span className="tabular-nums text-muted-foreground">
                Halaman {halamanIni.halaman} dari {halamanIni.jumlah}
              </span>
              <Button size="sm" variant="outline" disabled={halamanIni.halaman >= halamanIni.jumlah}
                      onClick={() => setHalaman(halamanIni.halaman + 1)}>Berikutnya</Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
